/**
 * StatusPill — 統一的狀態徽章（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 替代各頁面散落的 antd Tag，提供：
 *   - 7 種 tone（neutral / brand / success / warning / danger / info / muted）
 *   - dot（左側圓點，用於強調狀態變化）
 *   - subtle / solid / outline 三種視覺強度
 *
 * v3 升級：
 *   - 品牌色改為 forest green
 *   - bg 用 OKLCH 低彩度着色，更典雅
 *   - borderRadius = full（999px），pill 形態
 *   - font-variant-numeric: tabular-nums（數字對齊）
 */
import type { ReactNode } from 'react';
import { color, font, radius, text, weight } from '@/styles/tokens';

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

/** 每個 tone 的色彩映射：fg（文字）/ bg（背景）/ ring（邊框）/ dot（圓點） */
const TONE_PALETTE: Record<Tone, { fg: string; bg: string; ring: string; dot: string }> = {
  neutral: {
    fg: color.text.secondary,
    bg: color.surface.sunken,
    ring: color.border.default,
    dot: color.stone[400],
  },
  brand: {
    fg: color.brand.primary,
    bg: color.brand.primarySoft,
    ring: 'rgba(26, 133, 73, 0.20)' /* forest @ 20% */,
    dot: color.brand.primary,
  },
  success: {
    fg: color.emerald[700],
    bg: color.state.successSoft,
    ring: 'rgba(5, 150, 105, 0.22)',
    dot: color.state.success,
  },
  warning: {
    fg: color.amber[700],
    bg: color.state.warningSoft,
    ring: 'rgba(217, 119, 6, 0.24)',
    dot: color.state.warning,
  },
  danger: {
    fg: color.rose[700],
    bg: color.state.dangerSoft,
    ring: 'rgba(225, 29, 72, 0.22)',
    dot: color.state.danger,
  },
  info: {
    fg: color.sky[700],
    bg: color.state.infoSoft,
    ring: 'rgba(2, 132, 199, 0.22)',
    dot: color.state.info,
  },
  muted: {
    fg: color.text.tertiary,
    bg: 'transparent',
    ring: color.border.default,
    dot: color.text.muted,
  },
};

export interface StatusPillProps {
  /** 狀態文字 */
  children: ReactNode;
  /** 色調 */
  tone?: Tone;
  /** 是否在文字前顯示圓點（帶脈動感的狀態指示） */
  dot?: boolean;
  /** 視覺強度：subtle（淡背景）= 默認 / solid（實色）/ outline（無背景，只邊框） */
  variant?: 'subtle' | 'solid' | 'outline';
  /** 尺寸 */
  size?: 'sm' | 'md';
  /** 圖標（取代 dot 顯示） */
  icon?: ReactNode;
  /** 是否使用 mono 字體 + tabular numerals（數字計數場景） */
  mono?: boolean;
}

export default function StatusPill({
  children,
  tone = 'neutral',
  dot = false,
  variant = 'subtle',
  size = 'md',
  icon,
  mono,
}: StatusPillProps) {
  const palette = TONE_PALETTE[tone];

  /* 視覺風格分支 */
  let bg: string;
  let fg: string;
  let border: string;

  if (variant === 'solid') {
    bg = palette.dot;
    fg = '#fff';
    border = palette.dot;
  } else if (variant === 'outline') {
    bg = 'transparent';
    fg = palette.fg;
    border = palette.ring;
  } else {
    bg = palette.bg;
    fg = palette.fg;
    border = palette.ring;
  }

  /* 尺寸 */
  const fontSize = size === 'sm' ? text.xs : text.sm;
  const padInline = size === 'sm' ? 7 : 10;
  const padBlock = size === 'sm' ? 1 : 2;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
        borderRadius: radius.full,
        paddingInline: padInline,
        paddingBlock: padBlock,
        fontFamily: mono ? font.mono : font.body,
        fontSize,
        fontWeight: weight.semibold,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
      }}
    >
      {icon && (
        <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>{icon}</span>
      )}
      {!icon && dot && (
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: variant === 'solid' ? 'rgba(255,255,255,0.7)' : palette.dot,
            flexShrink: 0,
          }}
        />
      )}
      {children}
    </span>
  );
}
